import { HiChartPie } from "react-icons/hi";
import { Link, useLocation } from "react-router-dom";
import type { NavigationItem } from "../../types/navigation.ts";
import { useTranslation } from "../../hooks/useTranslation.ts";

interface SidebarNavigationProps {
  items?: NavigationItem[];
  className?: string;
}

interface NavigationGroup {
  items: NavigationItem[];
}

const SidebarNavigation = ({
  items = [],
  className = "",
}: SidebarNavigationProps) => {
  const location = useLocation();
  const { t } = useTranslation();

  const navigationGroups: NavigationGroup[] = [
    {
      items: [{ href: "/", icon: HiChartPie, label: t("navigation.overview") }],
    },
    {
      items: items,
    },
  ];

  // Custom sidebar item component for better control
  const CustomSidebarItem = ({ item }: { item: NavigationItem }) => {
    const isActive = location.pathname === item.href;

    return (
      <Link
        to={item.href}
        className={`group flex items-center w-full px-3 py-2 text-left rounded-lg transition-colors duration-150 text-decoration-none ${
          isActive
            ? "bg-blue-50 text-blue-700 border-r-2 border-blue-500"
            : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
        }`}
      >
        {item.icon && (
          <item.icon
            className={`flex-shrink-0 w-5 h-5 transition duration-75 mr-3 ${
              isActive
                ? "text-blue-500"
                : "text-gray-400 group-hover:text-gray-500"
            }`}
          />
        )}
        <span className="flex-1 text-sm font-medium truncate ml-1">
          {item.label}
        </span>
        {item.badge && (
          <span
            className={`inline-flex items-center justify-center px-2 ml-3 text-sm font-medium rounded-full ${
              isActive
                ? "text-blue-800 bg-blue-100"
                : "text-gray-800 bg-gray-200"
            }`}
          >
            {item.badge}
          </span>
        )}
      </Link>
    );
  };

  return (
    <div className={`h-full flex flex-col ${className}`}>
      <div className="h-full flex flex-col overflow-y-auto bg-white border-r border-gray-200">
        <div className="flex-1 px-3 py-4 space-y-1">
          {navigationGroups.map((group, groupIndex) => (
            <div key={groupIndex} className="space-y-1">
              {group.items.map((item, itemIndex) => (
                <CustomSidebarItem key={itemIndex} item={item} />
              ))}
              {groupIndex < navigationGroups.length - 1 && (
                <div className="my-4 border-t border-gray-200" />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SidebarNavigation;
